import React, { useMemo } from "react";
import { useIntl } from "umi";
import { List, ListItem } from "tea-component";
import NumberInput from "@/pages/datalab/components/NumberInput";
import TipContext from "@home/components/TipContext";
import { LANGUAGE_EN } from "@home/_config/config";
import { formatDate, getDateRange } from "@dbl/core/date";
import { GranularityRelativeOptionsConfig, type TDateGranularity, type TDateRange } from '@dbl/core/date';
import './index.scss';

/**
 * 相对时间选项值接口
 */
interface IRelativeTimeValue {
  nameCn: string;
  nameEn: string;
  amount: string;
  granularity: TDateGranularity;
  isSelectedCustom: boolean;
}

/**
 * 相对选项配置项接口
 */
interface IRelativeOptionConfig {
  textCn: string;
  textEn: string;
  value: string;
  amount: string;
  granularity: string;
}

/**
 * 计算后的相对选项接口
 */
interface IRelativeOption extends IRelativeOptionConfig {
  text: string;
}

/**
 * RelativeTimeOptions 组件 Props 接口
 */
interface IRelativeTimeOptionsProps {
  baseTimeRange: TDateRange;
  amount?: string;
  granularity?: TDateGranularity;
  timeGranularity: TDateGranularity;
  isSelectedCustom?: boolean;
  onChange: (value: IRelativeTimeValue, isClose: boolean) => void;
  extraList?: React.ReactNode;
}

/**
 * 主要用于 不同的对比分析时间选择器的相同的button显示
 * @param props
 * @constructor
 */
const RelativeTimeOptions = (props: IRelativeTimeOptionsProps) => {
  const { baseTimeRange, amount, granularity, timeGranularity, isSelectedCustom, onChange, extraList } = props;
  const { locale, formatMessage } = useIntl();
  const isEn = locale === LANGUAGE_EN;
  
  
  // 当基准时间范围或粒度变化时，重新计算当前选中的时间范围
  const options = useMemo(() => {
    const relativeOptions = (GranularityRelativeOptionsConfig[timeGranularity] || []) as IRelativeOptionConfig[];
    
    const options: IRelativeOption[] = relativeOptions.map(option => {
      const [startDate, endDate] = getDateRange(baseTimeRange, {amount: option.amount, granularity: option.granularity as TDateGranularity, timeGranularity});
      const start = formatDate(startDate, timeGranularity);
      const end = formatDate(endDate, timeGranularity);
      
      return {
        text: `${isEn ? option.textEn : option.textCn} (${start} ~ ${end})`,
        ...option,
      };
      
    });
    return options;
  }, [timeGranularity, baseTimeRange, isEn]);
  
  return (<List type="option" className={'dbl-rtrs-options-list'}>
    {
      options?.map((opt: IRelativeOption) => (<ListItem
        key={opt.text}
        className={'dbl-rtrs-option-item'}
        selected={!isSelectedCustom && ((opt.amount === amount && opt.granularity === granularity))}
        onClick={() => {
          const {textEn, textCn, amount, granularity} = opt;
          onChange({
            nameCn: textCn,
            nameEn: textEn,
            amount,
            granularity: granularity as TDateGranularity,
            isSelectedCustom: false,
          }, true);
        }}
      >
        {opt.text}
      </ListItem>))
    }
    
    <ListItem
      key={'input'}
      className={'dbl-rtrs-custom-item'}
      selected={!!(isSelectedCustom && amount && granularity)}
      onClick={() => {
        if (isSelectedCustom && amount) return;
        onChange({
          nameCn: '自定义',
          nameEn: 'Custom',
          isSelectedCustom: true,
          amount: '2',
          granularity: timeGranularity,
        }, true);
      }}
    >
      <span
        className={'dbl-rtrs-custom-label'}>{formatMessage({id: 'dbl_time_compare_custom_text_prefix'})}</span>
      <NumberInput
        type={'number'}
        className={'dbl-rtrs-custom-input'}
        value={(isSelectedCustom && amount) ? amount : '2'}
        onChange={(value) => {
          onChange({
            nameCn: '自定义',
            nameEn: 'Custom',
            isSelectedCustom: true,
            amount: String(value),
            granularity: timeGranularity,
          }, false);
        }}
        allowNegative={false}       // 禁止负数
        allowDecimal={false}        // 禁止小数
        min={1}                     // 正整数下限
        onMouseDown={(event) => {
          // Home仓的RangePicker组件内部 设置了preventMouseDown, 导致input框无法执行focus事件
          // 不要自己 preventDefault，只是阻断冒泡到祖先
          event.stopPropagation();
          event.nativeEvent.stopImmediatePropagation();
        }}
        onClick={(event) => {
          // 避免点击input框时关闭弹窗
          event.stopPropagation();
          event.nativeEvent.stopImmediatePropagation();
          // 当点击input框时, 选中自定义选项, 默认为2
          if (!isSelectedCustom) {
            onChange({
              nameCn: '自定义',
              nameEn: 'Custom',
              isSelectedCustom: true,
              amount: '2',
              granularity: timeGranularity,
            }, false);
          }
        }}
        onFocus={(event) => {
          // 避免点击input框时关闭弹窗
          event.stopPropagation();
          event.nativeEvent.stopImmediatePropagation();
        }}
      />
      <span
        className={'dbl-rtrs-custom-suffix'}>{formatMessage({id: `dbl_date_${timeGranularity}${isSelectedCustom ? (Number(amount) > 1 ? 's' : '') : 's'}`})} {formatMessage({id: 'dbl_date_ago'})}</span>
      <TipContext
        tipText={formatMessage({id: 'dbl_time_compare_custom_info'})}
        placementOffset={[6, 10]}
        overlayStyle={{zIndex: 999999999}}
      />
    </ListItem>
    
    {extraList}
    
  </List>);
};

export default RelativeTimeOptions;